<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('QUEUES_MGMT.SUPERVISOR.ASSIGN.TITLE')"
        :header-content="
          $t('QUEUES_MGMT.SUPERVISOR.ASSIGN.SUB_TEXT', {
            id: conversationIds.join(', #'),
          })
        "
      />
      <div class="p-8 pt-4">
        <woot-loading-state
          v-if="isLoading"
          :message="$t('QUEUES_MGMT.SUPERVISOR.ASSIGN.LOADING')"
        />
        <template v-else>
          <!-- QM §5.10 — multiselect -->
          <label>
            {{ $t('QUEUES_MGMT.SUPERVISOR.ASSIGN.AGENT_LABEL') }}
          </label>
          <multiselect
            v-model="selectedAgent"
            :options="agentOptions"
            track-by="id"
            label="name"
            :close-on-select="true"
            :allow-empty="false"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
            :placeholder="$t('QUEUES_MGMT.SUPERVISOR.ASSIGN.AGENT_LABEL')"
          />

          <!-- QM §4.7.1 — assigning outside the queue's groups is allowed but audited. -->
          <p
            v-if="isOutsideRoutingGroups"
            class="mt-2 text-sm text-yellow-700 dark:text-yellow-500"
          >
            {{ $t('QUEUES_MGMT.SUPERVISOR.ASSIGN.OUTSIDE_WARNING') }}
          </p>

          <div class="flex justify-end items-center py-2 px-0 gap-2 w-full">
            <woot-button class="button clear" @click.prevent="onClose">
              {{ $t('QUEUES_MGMT.SUPERVISOR.ASSIGN.CANCEL') }}
            </woot-button>
            <woot-button
              :is-disabled="!selectedAgent || isAssigning"
              :is-loading="isAssigning"
              @click="assign"
            >
              {{ $t('QUEUES_MGMT.SUPERVISOR.ASSIGN.CONFIRM') }}
            </woot-button>
          </div>
        </template>
      </div>
    </div>

    <!-- QM §5.5.3 — reassigning a locked ticket releases the lock; confirm first. -->
    <woot-confirm-modal
      ref="lockConfirm"
      :title="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.TITLE')"
      :description="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.DESC')"
      :confirm-label="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.YES')"
      :cancel-label="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.NO')"
    />
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import ConversationApi from 'dashboard/api/inbox/conversation';

export default {
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // display_ids of the tickets being assigned — one, or several via bulk select (§5.5.2).
    conversationIds: {
      type: Array,
      required: true,
    },
    // The queue the ticket is waiting in — supplies the routing groups.
    queue: {
      type: Object,
      required: true,
    },
    // QM §5.5.3 — set when reassigning a single already-locked ticket (Assigned tab).
    isLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedAgent: null,
      isLoading: false,
      isAssigning: false,
    };
  },
  computed: {
    ...mapGetters({
      agents: 'agents/getVerifiedAgents',
    }),
    routingGroupMemberIds() {
      const teamIds = [
        this.queue.primary_team_id,
        this.queue.overflow_team_id,
      ].filter(Boolean);
      return teamIds.flatMap(teamId =>
        this.$store.getters['teamMembers/getTeamMembers'](teamId).map(
          member => member.id
        )
      );
    },
    // QM §4.7.1 manual override - Every agent is selectable, but out-of-group ones
    // are labelled so the supervisor knows the assignment bypasses the queue.
    agentOptions() {
      return this.agents.map(agent => ({
        ...agent,
        name: this.routingGroupMemberIds.includes(agent.id)
          ? agent.name
          : `${agent.name} · ${this.$t(
              'QUEUES_MGMT.SUPERVISOR.ASSIGN.OUTSIDE_BADGE'
            )}`,
      }));
    },
    isOutsideRoutingGroups() {
      if (!this.selectedAgent) {
        return false;
      }
      return !this.routingGroupMemberIds.includes(this.selectedAgent.id);
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.selectedAgent = null;
        this.load();
      }
    },
  },
  mounted() {
    if (this.show) {
      this.load();
    }
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const teamIds = [
          this.queue.primary_team_id,
          this.queue.overflow_team_id,
        ].filter(Boolean);
        await Promise.all([
          this.$store.dispatch('agents/get'),
          ...teamIds.map(teamId =>
            this.$store.dispatch('teamMembers/get', { teamId })
          ),
        ]);
      } catch (error) {
        this.showAlert(this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    // QM §5.5.2 — bulk assignment is one request per ticket, so each resulting state
    // change produces its own audit entry, as the PRD requires.
    async assign() {
      // QM §5.5.3 — the backend releases the lock silently for a supervisor; the
      // confirmation has to happen here, before the request is even sent.
      if (this.isLocked) {
        const confirmed = await this.$refs.lockConfirm.showConfirmation();
        if (!confirmed) return;
      }
      this.isAssigning = true;
      const assigned = [];
      let failed = 0;
      for (const conversationId of this.conversationIds) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await ConversationApi.assignAgent({
            conversationId,
            agentId: this.selectedAgent.id,
          });
          assigned.push(conversationId);
        } catch (error) {
          failed += 1;
        }
      }
      this.isAssigning = false;
      this.reportResult(assigned, failed);
    },
    reportResult(assigned, failed) {
      if (assigned.length) {
        this.showAlert(
          this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.ASSIGNED', {
            id: assigned.join(', #'),
          })
        );
        this.$emit('assigned', assigned);
      }
      if (failed) {
        this.showAlert(
          this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.BULK_FAILED', { count: failed })
        );
      }
      this.onClose();
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>
